import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Upload, FileText, Calendar, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function Assignments() {
    const { user } = useAuth();
    const [items, setItems] = useState([]);
    const [active, setActive] = useState(null);
    const [file, setFile] = useState(null);
    const load = async () => { const { data } = await api.get("/assignments"); setItems(data.items); };
    useEffect(() => { load(); }, []);

    const submit = async () => {
        if (!file || !active) return;
        const fd = new FormData();
        fd.append("file", file);
        try {
            await api.post(`/assignments/${active.id}/submit`, fd);
            toast.success("Submission uploaded");
            setActive(null); setFile(null);
            load();
        } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    };

    return (
        <div className="space-y-6" data-testid="assignments-page">
            <div>
                <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Work</div>
                <h1 className="font-display text-3xl lg:text-4xl mt-1">Assignments</h1>
            </div>
            <div className="grid gap-4">
                {items.map(a => {
                    const sub = a.my_submission;
                    return (
                        <Card key={a.id} className="mx-card" data-testid={`assignment-${a.id}`}>
                            <CardContent className="p-5 flex flex-wrap items-start gap-4">
                                <div className="h-12 w-12 rounded-xl bg-[#F0EFFF] dark:bg-[#1C1A2E] flex items-center justify-center"><FileText className="h-5 w-5 text-[#726AF0]" /></div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">{a.course_title}</div>
                                    <div className="font-bold text-base mt-1">{a.title}</div>
                                    <div className="text-sm text-muted-foreground mt-1 line-clamp-2">{a.instructions}</div>
                                    <div className="flex flex-wrap gap-2 mt-3 items-center">
                                        {a.due_date && <Badge variant="outline" className="text-[10px]"><Calendar className="h-3 w-3 mr-1" /> Due {new Date(a.due_date).toLocaleDateString()}</Badge>}
                                        <Badge variant="outline" className="text-[10px]">Max: {a.max_marks}</Badge>
                                        {sub && (
                                            <Badge className={
                                                sub.status === "APPROVED" ? "bg-[#16A34A] hover:bg-[#16A34A] text-[10px]" :
                                                    sub.status === "GRADED" ? "bg-[#FFA81B] hover:bg-[#FFA81B] text-[10px]" : "bg-[#726AF0] hover:bg-[#726AF0] text-[10px]"
                                            } data-testid={`assignment-status-${a.id}`}>
                                                {sub.status} {sub.score != null && `· ${sub.score}/${a.max_marks}`}
                                            </Badge>
                                        )}
                                        {sub?.is_late && <Badge className="bg-[#E11D48] hover:bg-[#E11D48] text-[10px]">LATE</Badge>}
                                    </div>
                                    {sub?.remarks && <div className="mt-3 p-3 bg-muted rounded-xl text-sm"><strong>Remarks: </strong>{sub.remarks}</div>}
                                </div>
                                {user.role === "STUDENT" && (
                                    <Dialog open={active?.id === a.id} onOpenChange={(v) => { setActive(v ? a : null); if (!v) setFile(null); }}>
                                        <DialogTrigger asChild>
                                            <Button className="mx-btn-primary" data-testid={`submit-assignment-btn-${a.id}`}><Upload className="h-4 w-4 mr-2" /> {sub ? "Resubmit" : "Submit"}</Button>
                                        </DialogTrigger>
                                        <DialogContent>
                                            <DialogHeader>
                                                <DialogTitle>Submit assignment</DialogTitle>
                                                <DialogDescription>Allowed: PDF, DOC, DOCX, ZIP — max 25 MB.</DialogDescription>
                                            </DialogHeader>
                                            <Input type="file" accept=".pdf,.doc,.docx,.zip" onChange={(e) => setFile(e.target.files[0])} data-testid="submission-file-input" />
                                            <DialogFooter><Button onClick={submit} disabled={!file} className="mx-btn-primary" data-testid="submission-submit-btn">Upload</Button></DialogFooter>
                                        </DialogContent>
                                    </Dialog>
                                )}
                            </CardContent>
                        </Card>
                    );
                })}
                {items.length === 0 && <div className="mx-card p-12 text-center text-muted-foreground"><CheckCircle2 className="h-10 w-10 mx-auto mb-3 text-[#16A34A]" />No assignments yet.</div>}
            </div>
        </div>
    );
}
